import { get, set } from "lodash";
import React from "react";
import { Form as FinalForm, FormSpy } from "react-final-form";
import { connect } from "react-redux";
import { Col, Row } from "reactstrap";

import { UserRole } from "../../store/models/user.model";
import { IRootState } from "../../store/reducers";
import { createCompany, fetchCompany, updateCompany } from "../../store/reducers/companies";
import { fetchCompanyOptions } from "../../store/reducers/companyBuilder";

import { FORM_ERROR } from "final-form";
import { RouteComponentProps } from "react-router";
import NumberFormatField from "../../components/Form/NumberFormatFieldV2/NumberFormatFieldV2";
import SingleSelectField from "../../components/Form/SingleSelectFieldV2/SingleSelectField";
import TextField from "../../components/Form/TextFieldV2/TextFieldV2";
import TypeaheadField, { ITypeaheadOption } from "../../components/Form/TypeaheadFieldV2/TypeaheadFieldV2";
import * as S from "../../constants/StringConstants";
import "./CompanyBuilder.scss";

import formatString from "format-string-by-pattern";
import * as P from "../../util/utils.validators";

import AppBar from "src/components/AppBar/AppBar";
import { IconName } from "src/components/Icon/Icon";
import Toast from "src/components/Toast/Toast";
import { keyValueToDropdownOption } from "src/util/util.mapDropdownOptions";
import { Container as DetailContainer } from "../../components/Container/Container";
import { addDefaultOption, undefinedIfDefault } from "../../util/utils.defaultValues";
import CompanyQuotesWidget from "./CompanyQuotesWidget";

interface ICompanyBuilderProps extends StateProps, DispatchProps, RouteComponentProps<{ id: string }> {}

class CompanyBuilder extends React.Component<ICompanyBuilderProps> {
  validate = (values: any) => {
    const errors = {};

    if (P.isEmpty(values.name)) {
      set(errors, "name", S.FORM_FIELD_REQUIRED);
    }

    const zipCode = get(values, "address.zip");
    const zipPattern = P.ZIP_CODE_REGEX;
    if (zipCode && !zipPattern.test(zipCode)) {
      set(errors, "address.zip", S.FORM_FIELD_INVALID_ZIPCODE);
    }

    return errors;
  };

  companyToFormValues(company: any, affiliates: ITypeaheadOption[]) {
    if (!company || !affiliates || !affiliates.length) {
      return company;
    }
    const affiliate = affiliates.find(a => a.value === get(company, "affiliateID"));

    if (affiliate) {
      company.affiliateID = [affiliate];
    } else {
      company.affiliateID = undefined;
    }

    return company;
  }

  formValuesToCompany(values: any) {
    if (!values) {
      return values;
    }

    let affiliateIDs = get(values, "affiliateID");

    values.branchTypeID = undefinedIfDefault(values.branchTypeID);
    values.rbManagerID = undefinedIfDefault(values.rbManagerID);
    if (values.address) {
      values.address.stateID = undefinedIfDefault(values.address.stateID);
    }

    if (affiliateIDs && affiliateIDs.length && affiliateIDs[0].value) {
      values.affiliateID = affiliateIDs[0].value;
    } else {
      values.affiliateID = undefined;
    }

    return values;
  }

  onSubmit = async (values: any) => {
    const company = this.formValuesToCompany(values);
    try {
      if (this.companyID) {
        await this.props.updateCompany(company);
      } else {
        await this.props.createCompany(company);
      }
    } catch (err) {
      return { [FORM_ERROR]: "Error Submitting" };
    }

    // TODO: Get this path from a library/directory
    this.props.history.push("/companies/all");
  };

  async componentDidMount() {
    await this.props.fetchCompanyOptions();

    // check match for id param
    const id = this.companyID;
    if (id) {
      await this.props.fetchCompany(this.companyID.toString());
    }
  }

  get companyID() {
    const id = parseInt(this.props.match.params.id, 10);

    return id && !isNaN(id) ? id : null;
  }

  getAffiliateButtonLabel(form: any) {
    const affIDField = form.getFieldState("affiliateID");
    if (!affIDField || !affIDField.value || !affIDField.value.length) {
      return S.CO_ADD_AFFILIATE_BUTTON;
    }

    return S.CO_AFFILIATE_DETAILS_BUTTON;
  }

  handleAffiliateDetailsClick = (e, form) => {
    e.preventDefault();
    const affIDField = form.getFieldState("affiliateID");
    if (affIDField && affIDField.value && affIDField.value.length) {
      window.open(`${window.location.origin}/affiliates/${affIDField.value[0].value}`);
    } else {
      window.open(`${window.location.origin}/affiliates/new`);
    }
  };

  render() {
    const { dropdownOptions, companies } = this.props;
    const phoneMask = { name: "phone-1", parse: "(999) 999-9999" };
    const zipMask = { name: "address.zip", parse: "00000" };

    // if there's an id then we should be editing
    const company = companies.map[this.companyID];
    const { user } = this.props;

    const defaultValues = {
      branchTypeID: undefined,
    };

    const affiliateList = (get(dropdownOptions, "affiliateList") || []).map(keyValueToDropdownOption);

    const affiliate = affiliateList.find(a => a.value === get(company, "affiliateID"));
    const branchList = addDefaultOption("", get(dropdownOptions, "branchList", [])) || [];
    const rbmList = addDefaultOption("", get(dropdownOptions, "rbmList", []));
    const stateList = addDefaultOption("", get(dropdownOptions, "selectStateList", []));

    const initialValues = this.companyToFormValues(company, affiliateList) || defaultValues;

    var affiliateButton = null;
    if (user.userRole === UserRole.Admin) {
      affiliateButton = (
        <FormSpy
          render={({ form }) => (
            <Col sm="3">
              <button
                className="button-v2 button-v2__white button-v2__secondary-mini"
                onClick={e => this.handleAffiliateDetailsClick(e, form)}>
                {this.getAffiliateButtonLabel(form)}
              </button>
            </Col>
          )}
        />
      );
    }

    return (
      <>
        <FinalForm
          validate={this.validate}
          onSubmit={this.onSubmit}
          initialValues={initialValues}
          render={({ handleSubmit, submitting, invalid }) => {
            return (
              <>
                <AppBar icon={IconName.Companies} title={S.CO_DETAIL_PAGE_TITLE} />

                <form onSubmit={handleSubmit}>
                  <div className="company-builder__form">
                    <section className="company-builder__wrap">
                      <DetailContainer>
                        <div className="company-builder__detail-form">
                          <Col className="app-form-header">
                            <h4 className="form-title">{S.CO_SECOND_TITLE}</h4>
                          </Col>

                          <Row>
                            <Col sm="6">
                              <TextField name="name" label={S.CO_NAME_LABEL} key="company.name-key" />
                            </Col>
                            <Col sm="3">
                              <TextField
                                name="companyID"
                                disabled
                                label={S.CO_COMPANY_ID_LABEL}
                                key="company.company-id-key"
                              />
                            </Col>
                            <Col sm="3">
                              <NumberFormatField
                                name="externalID"
                                label={S.CO_EXTERNAL_ID_LABEL}
                                key="company.external-id-key"
                              />
                            </Col>
                          </Row>
                          <Row>
                            <Col sm="3">
                              <SingleSelectField
                                name="branchTypeID"
                                label={S.CO_BRANCH_LABEL}
                                key="company.branch-id-key"
                                options={branchList}
                              />
                            </Col>
                            <Col sm="3">
                              <SingleSelectField
                                name="rbManagerID"
                                label={S.CO_RBM_LABEL}
                                key="company.rbm-id-key"
                                options={rbmList}
                              />
                            </Col>
                            <Col sm="3">
                              <FormSpy>
                                {({ values }) => {
                                  const selectedAffiliate = values.affiliateID ? values.affiliateID[0] : null;
                                  return (
                                    <TypeaheadField
                                      name="affiliateID"
                                      label={S.CO_AFFILIATE_LABEL}
                                      key="company.affiliateID-key"
                                      options={affiliateList}
                                      selected={selectedAffiliate}
                                    />
                                  );
                                }}
                              </FormSpy>
                            </Col>
                          </Row>
                          <Row>
                            <Col sm="3">
                              <TextField
                                name="emailAddress"
                                label={S.CO_EMAIL_LABEL}
                                key="company.email-key"
                                type="email"
                              />
                            </Col>
                            <Col sm="3">
                              <TextField
                                name="phoneNumber"
                                label={S.CO_PHONE_LABEL}
                                key="company.phone-key"
                                type="text"
                                parse={formatString(phoneMask.parse)}
                              />
                            </Col>

                            <Col sm="3"></Col>
                            {affiliateButton}
                          </Row>

                          <h4 className="form-title mb-4">{S.CO_THIRD_TITLE}</h4>

                          <Row>
                            <Col sm="3">
                              <TextField
                                name="primaryContact.firstName"
                                label={S.CO_FIRST_NAME_LABEL}
                                key="company.first-name-key"
                              />
                            </Col>
                            <Col sm="3">
                              <TextField
                                name="primaryContact.lastName"
                                label={S.CO_LAST_NAME_LABEL}
                                key="company.last-name-key"
                              />
                            </Col>
                          </Row>

                          <h4 className="form-title mb-4">{S.CO_FOURTH_TITLE}</h4>

                          <Row>
                            <Col sm="3">
                              <TextField
                                name="address.streetAddress"
                                label={S.CO_STREET_ADDRESS_LABEL}
                                key="company.address-key"
                              />
                            </Col>
                            <Col sm="3">
                              <TextField name="address.city" label={S.CO_CITY_LABEL} key="company.city-key" />
                            </Col>
                            <Col sm="3">
                              <SingleSelectField
                                name="address.stateID"
                                label={S.CO_STATE_LABEL}
                                key="company.state-key"
                                options={stateList}
                              />
                            </Col>
                            <Col sm="3">
                              <TextField name="address.zip" label={S.CO_ZIP_LABEL} key="company.zip-key" type="text" />
                            </Col>
                          </Row>

                          <div className="company-builder__button-wrap">
                            <button
                              className="button-v2 button-v2__secondary"
                              type="button"
                              onClick={() => this.props.history.goBack()}>
                              {S.CO_CANCEL_BUTTON}
                            </button>
                            <button className="button-v2 button-v2__primary" type="submit" disabled={submitting}>
                              {S.CO_SUBMIT_BUTTON}
                            </button>
                          </div>
                        </div>
                      </DetailContainer>
                    </section>

                    <aside>
                      <CompanyQuotesWidget />
                    </aside>
                  </div>
                </form>
              </>
            );
          }}
        />

        <Toast message={S.LOADING} onClose={() => {}} open={this.props.loading} />
      </>
    );
  }
}

const mapStateToProps = ({
  auth,
  companies,
  affiliates,
  companyBuilder: { loading, ...dropdownOptions },
}: IRootState) => ({
  user: auth.user,
  companies,
  affiliates,
  loading,
  dropdownOptions,
});

const mapDispatchToProps = { createCompany, fetchCompanyOptions, fetchCompany, updateCompany };

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = typeof mapDispatchToProps;

export default connect(mapStateToProps, mapDispatchToProps)(CompanyBuilder);
